import React from "react";

import { CategoryRounded, KeyboardArrowDownRounded, SearchRounded } from "@mui/icons-material";
import { Box, ButtonBase, Divider, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useModal from "../../../../config/context/modalContext";
import { NotificationLogService } from "../../../../config/services/NotificationLogService";
import { GreyPallet } from "../../../../config/styles";
import { Dev } from "../../../../config/util/consoleUtil";

import ChipMain from "../../../../components/InputMain/ChipMain";
import MainModalSimple from "../../../../components/MainModalSimple";
import MainSolidButton from "../../../../components/MainSolidButton";
import MainText from "../../../../components/MainText";

import MainBorderButton from "../../../../components/MainBorderButton";
import { Constants } from "../../../../config/constants";
import { ModalBaseType } from "../../../../types/common";

export default function ModalHistory({ onClose }: ModalBaseType) {
  const navigate = useNavigate();

  const { pushAlert } = useModal();

  const logList = NotificationLogService.LazyList();

  const [firstLoading, setFirstLoading] = React.useState(false);

  const allTopicNames = [
    ...Constants.publicTopics.map((item) => item.topic),
    ...Constants.privateTopics.map((item) => item.topic),
  ];

  const [selectedTopics, setSelectedTopics] = React.useState(allTopicNames);

  const handleFetchHistory = async () => {
    try {
      setFirstLoading(true);
      await logList.runRequest();
    } catch (error: any) {
      console.log(...new Dev("handleFetchHistory", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter histórico",
      });
    } finally {
      setFirstLoading(false);
    }
  };

  React.useEffect(() => {
    handleFetchHistory();
  }, []);

  return (
    <MainModalSimple title="Histórico" isClosable onClose={() => onClose?.()}>
      <Box>
        <Box mb={2} gap={1} display={"flex"} flexWrap={"wrap"}>
          <ChipMain
            options={allTopicNames.map((item) => ({ name: item, value: item }))}
            onChange={(value: any) => {
              if (selectedTopics.includes(value)) {
                setSelectedTopics(selectedTopics.filter((item) => item !== value));
              } else {
                setSelectedTopics([...selectedTopics, value]);
              }
            }}
            value={selectedTopics}
            multiple
          />
        </Box>
        <Box sx={{ mb: 4 }}>
          <MainBorderButton
            small
            loading={logList.isLoading}
            text="Pesquisar"
            icon={<SearchRounded />}
            onClick={() => logList.runRequest(selectedTopics, true)}
          />
        </Box>

        {firstLoading &&
          [90, 120].map((item) => (
            <Skeleton
              key={item}
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={item}
              sx={{ borderRadius: "15px", mb: 2 }}
            />
          ))}

        {logList.items.map((item) => (
          <ButtonBase
            key={item.id}
            sx={{
              borderRadius: "15px",
              overflow: "hidden",
              marginBottom: "20px",
              width: "100%",
            }}
          >
            <Box
              sx={{
                border: `3px solid ${GreyPallet.Light}`,
                borderRadius: "15px",
                width: "100%",
                overflow: "hidden",
                "& *": { boxSizing: "border-box" },
              }}
            >
              <Box
                style={{
                  textAlign: "start",
                  width: "100%",
                }}
                py={1}
              >
                <Box
                  px={2}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <MainText color="regular" size="small">
                    {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                  </MainText>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CategoryRounded sx={{ mr: 1, fontSize: "inherit" }} />
                    <MainText color="black" size="small">
                      {item.topic}
                    </MainText>
                  </Box>
                </Box>

                <Divider
                  sx={{
                    my: 1,
                    border: `1px solid ${GreyPallet.Light}`,
                    borderRadius: "1px",
                  }}
                />

                <Box
                  px={2}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <MainText color="black" size="body">
                    {item.title}
                  </MainText>

                  <MainText color="regular" size="caption">
                    {item.body}
                  </MainText>
                </Box>
              </Box>
            </Box>
          </ButtonBase>
        ))}

        {logList.hasMore && !firstLoading && (
          <MainSolidButton
            loading={logList.isLoading}
            text="Carregar Mais"
            icon={<KeyboardArrowDownRounded />}
            onClick={() => logList.runRequest(selectedTopics)}
          />
        )}
      </Box>
    </MainModalSimple>
  );
}
